// questionnaireSlice.ts
import {createAsyncThunk, createSlice, PayloadAction} from "@reduxjs/toolkit";
import {QuestionnaireConfig} from "../data/QuestionConfig";
import type {RootState} from "./store";
import {
    EBike,
    QuestionnaireBicycleType,
    QuestionnaireType
} from "../data/AnswerEnums.ts";
import {apolloClient} from "../apollo.ts";
import {
    AddAppEventDocument,
    AddNewsletterSubscriptionDocument,
    CreateNewQuestionnaireDocument,
    EvaluateCustomerAnswerDocument, InsertFeedbackDocument, SendRecommendationEmailDocument,
    UpsertCustomerAnswerDocument
} from "../generated/graphql.tsx";
import {AdvisorConfig, AdvisorConfigs} from "../data/AdvisorConfig.ts";


//FIXME better naming
export enum QuestionnaireStates {
    Uninitialized,
    Creating,
    CreateError,
    Created,
    Submitting,
    SubmitError,
    Submitted,
}

type Answers = Record<string, (string | number)[]>;

export type KeyValuePair<K extends string, V> = [K, V];

interface QuestionnaireState {
    questionnaireId: string | null;
    state: QuestionnaireStates;
    currentQuestion: number;

    currentAdvisorConfig?: AdvisorConfig; // Neue Property

    answers: Answers;

    filteredQuestions: string[];

    selectedQuestionnaireTypes: QuestionnaireType[];

    oneTimeToken: string | null;
}



function answersToGraphqlVariables(answers: Answers): Record<string, unknown> {
    function getValueOrNull(name: string): string | number | null {
        const answer = answers[name] as (string | number)[] | undefined;
        if (answer === undefined || answer.length !== 1) {
            return null;
        }
        return answer[0];
    }

    function getValuesAsGraphqlInput(name: string, fieldName: string): Record<string, string>[] {
        const answer = answers[name] as string[] | undefined;
        const values = answer ?? [];
        return values.map((v) => ({[fieldName]: v}));
    }


    return {
        sex: getValueOrNull("sex"),
        bicycle_type: getValueOrNull("bicycle_type"),
        e_bike: getValueOrNull("e_bike") === EBike.eBikeYes,
        driving_frequency: getValueOrNull("driving_frequency"),
        driving_duration: getValueOrNull("driving_duration"),
        seat_position: getValueOrNull("seat_position"),
        weight: getValueOrNull("weight") ? parseInt(String(getValueOrNull("weight")), 10) : null,
        pelvic_problems: getValuesAsGraphqlInput("pelvic_problems", "pelvic_problem_id"),
        hand_problems: getValuesAsGraphqlInput("hand_problems", "hand_problem_id"),
        leg_axis: getValueOrNull("leg_axis"),
        foot_type: getValueOrNull("foot_type"),
        foot_position: getValueOrNull("foot_position"),
        foot_problems: getValuesAsGraphqlInput("foot_problems", "foot_problem_id"),
        shoe_size: getValueOrNull("shoe_size_eu") !== null
            ? parseInt(String(getValueOrNull("shoe_size_eu")), 10)
            : null,
        grip_width_cm: getValueOrNull("grip_width_cm"),
        hand_width_cm: getValueOrNull("hand_width_cm"),
        body_height: getValueOrNull("body_height"),
        arm_length_cm: getValueOrNull("$arm_length_cm"),
        shoulder_width_cm: getValueOrNull("shoulder_width_cm"),
        step_length: getValueOrNull("step_length"),
        hip_circumference: getValueOrNull("hip_circumference"),
        seat_bone_distance_cm: getValueOrNull("seat_bone_distance_cm"),
        email: getValueOrNull("email"),
        name: getValueOrNull("name"),
    };
}

export const startQuestionnaire = createAsyncThunk<
    string,
    { questionnaireTypes?: QuestionnaireType[]; bicycleType?: QuestionnaireBicycleType }
>(
    "questionnaire/start",
    async ({ questionnaireTypes, bicycleType }, thunkAPI): Promise<string> => {
        thunkAPI.dispatch(start({ questionnaireTypes, bicycleType }));
        const mutationResult = await apolloClient.mutate({
            mutation: CreateNewQuestionnaireDocument,
        });
        const id = mutationResult.data?.createAnswerPublicUser?.answerId;
        const oneTimeToken = mutationResult.data?.createAnswerPublicUser?.oneTimeToken;

        if (id && oneTimeToken) {
            console.log("Started new questionnaire with id: ", id);
            console.log("and OneTimeToken: ", oneTimeToken);

            // Speichere Questionnaire im Redux-State
            thunkAPI.dispatch(setQuestionnaireId(id));
            thunkAPI.dispatch(setOneTimeToken(oneTimeToken));

            // Aktualisiere die URL: setze den "onetimetoken" Parameter
            const currentUrl = new URL(window.location.href);
            currentUrl.searchParams.set("onetimetoken", oneTimeToken);

            // Füge den Token als URL-Parameter hinzu
            // currentUrl.searchParams.set("questionId", id);

            // Aktualisiere die URL ohne Neuladen der Seite
            window.history.pushState({}, '', currentUrl.toString());

            // Log successful questionnaire creation
            thunkAPI.dispatch(logAppEvent({
                eventName: "WEB_QUESTIONNAIRE_CREATED",
                level: "INFO",
                answer_id: id,
                payload: {
                    questionnaireId: id,
                    oneTimeToken: oneTimeToken,
                    questionnaireTypes: questionnaireTypes || [QuestionnaireType.whole],
                    bicycleType: bicycleType || null,
                    timestamp: new Date().toISOString()
                }
            }));

            return id;
        } else {
            thunkAPI.rejectWithValue("Could not retrieve ID or One-Time Token");
            return "";
        }
    }
);


// Event-Logging Funktion
export const logAppEvent = createAsyncThunk<
    void,
    { eventName: string; level?: string; payload?: Record<string, unknown>; answer_id: string }
>(
    "questionnaire/logAppEvent",
    async ({ eventName, level = "DEBUG", payload = {}, answer_id }) => {
        try {
            await apolloClient.mutate({
                mutation: AddAppEventDocument,
                variables: {
                    answer_id: answer_id,
                    event_name: eventName,
                    level: level as never,
                    payload: payload
                },
            });
        } catch (error) {
            console.error(`Fehler beim Loggen des Events ${eventName}:`, error);
            // Event-Logging sollte die Hauptfunktionalität nicht beeinträchtigen
        }
    }
);


export const submitAnswers = createAsyncThunk<void, void>(
    "questionnaire/submit",
    async (_, thunkApi): Promise<void> => {
        const state = thunkApi.getState() as RootState;
        const variables = answersToGraphqlVariables(state.questionnaire.answers);
        variables["id"] = state.questionnaire.questionnaireId as string;

        // Log questionnaire submission start
        thunkApi.dispatch(logAppEvent({
            eventName: "WEB_QUESTIONNAIRE_SUBMIT",
            answer_id: state.questionnaire.questionnaireId as string,
            level: "INFO",
            payload: {
                questionnaireId: state.questionnaire.questionnaireId,
                questionnaireType: state.questionnaire.selectedQuestionnaireTypes[0] || QuestionnaireType.whole,
                totalAnswers: Object.keys(state.questionnaire.answers).length,
                timestamp: new Date().toISOString()
            }
        }));

        await apolloClient.mutate({
            mutation: UpsertCustomerAnswerDocument,
            variables,
        });
    }
);

export const sendDataToWebhook = createAsyncThunk<void, {email: string, subscribeNewsletter: boolean}>(
    "questionnaire/sendToWebhook",
    async (params, thunkApi): Promise<void> => {
        try {
            const state = thunkApi.getState() as RootState;
            const answers = state.questionnaire.answers;
            const { email, subscribeNewsletter } = params;

            const webhookData = {
                answer_id: state.questionnaire.questionnaireId,
                sex: answers.sex,
                bicycle_type: answers.bicycle_type,
                e_bike: answers.e_bike,
                driving_frequency: answers.driving_frequency,
                driving_duration: answers.driving_duration,
                seat_position: answers.seat_position,
                weight: answers.weight,
                pelvic_problems: answers.pelvic_problems || [],
                hand_problems: answers.hand_problems || [],
                leg_axis: answers.leg_axis,
                foot_type: answers.foot_type,
                foot_position: answers.foot_position,
                foot_problems: answers.foot_problems || [],
                shoe_size: answers.shoe_size,
                grip_width_cm: answers.grip_width_cm,
                hand_width_cm: answers.hand_width_cm,
                body_height: answers.body_height,
                arm_length_cm: answers.arm_length_cm,
                shoulder_width_cm: answers.shoulder_width_cm,
                step_length: answers.step_length,
                hip_circumference: answers.hip_circumference,
                seat_bone_distance_cm: answers.seat_bone_distance_cm,
                email: email,
                newsletter_subscribed: subscribeNewsletter
            };

            // Log für Debugging-Zwecke
            console.log("===== WEBHOOK DATA =====");
            console.log(JSON.stringify(webhookData, null, 2));
            console.log("===== END WEBHOOK DATA =====");

            const response = await fetch('https://hook.eu2.make.com/mwjqjxwbcweijb7lbeybhty4e7k038u2', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(webhookData)
            });

            if (!response.ok) {
                throw new Error(`Webhook call failed with status: ${response.status}`);
            }

            const responseData = await response.text();
            console.log("Webhook response:", responseData);
        } catch (error) {
            console.error("Error sending data to webhook:", error);
            // Re-throw to allow the calling code to handle the error if needed
            throw error;
        }
    }
);




// In deiner Redux-Slice-Datei oder einer separaten Thunks-Datei
export const submitFeedback = createAsyncThunk<void, {rating: string, comment: string}>(
    "questionnaire/submitFeedback",
    async (feedbackData, thunkApi): Promise<void> => {
        const state = thunkApi.getState() as RootState;
        const questionnaireId = state.questionnaire.questionnaireId as string;

        await apolloClient.mutate({
            mutation: InsertFeedbackDocument,
            variables: {
                rating: feedbackData.rating,
                comment: feedbackData.comment,
                questionnaire_id: questionnaireId
            },
        });
    }
);




export const evaluateAnswers = createAsyncThunk<void, void>(
    "questionnaire/evaluate",
    async (_, thunkApi): Promise<void> => {
        const state = thunkApi.getState() as RootState;
        console.log("Evaluate... current answer id: ", state.questionnaire.questionnaireId as string);
        const variables = { "customerAnswerId": state.questionnaire.questionnaireId as string };

        await apolloClient.mutate({
            mutation: EvaluateCustomerAnswerDocument,
            variables,
        });

        // Log questionnaire submission start
        thunkApi.dispatch(logAppEvent({
            eventName: "WEB_QUESTIONNAIRE_EVALUATE",
            answer_id: state.questionnaire.questionnaireId as string,
            level: "INFO",
            payload: {
                questionnaireId: state.questionnaire.questionnaireId,
                questionnaireType: state.questionnaire.selectedQuestionnaireTypes[0] || QuestionnaireType.whole,
                totalAnswers: Object.keys(state.questionnaire.answers).length,
                timestamp: new Date().toISOString()
            }
        }));
    }
);

/**
 * Diese createAsyncThunk wird ausgeführt, wenn der User am Ende des Fragebogens eine
 * E-Mail angegeben hat. Wenn keine E-Mail hinterlegt wird, passiert nichts.
 */
export const sendRecommendationEmail = createAsyncThunk<
    void,
    { email: string; region: string }
>(
    "questionnaire/sendRecommendationEmail",
    async ({ email, region }, thunkApi) => {
        // Falls keine E-Mail vorliegt (oder leer), Abbruch
        if (!email) return;

        const state = thunkApi.getState() as RootState;
        const questionnaireId = state.questionnaire.questionnaireId;

        // Sprache aus region ableiten, hier simplifiziert
        // Du kannst diese Logik beliebig anpassen
        let language = "DE";
        if (region.includes("/EN")) {
            language = "EN";
        }

        try {
        const variables = { "customerAnswerId": questionnaireId, "language": language };
        await apolloClient.mutate({
            mutation: SendRecommendationEmailDocument,
            variables,
        });
        }
        catch (e) {
            console.log(`EMAIL ERROR: ${e}`);
        }


    }
);


export const addNewsletterSubscription = createAsyncThunk<
    void,
    { email: string; region: string }
>(
    "questionnaire/addNewsletterSubscription",
    async ({ email, region }) => {
        // Falls keine E-Mail vorliegt (oder leer), Abbruch
        if (!email) return;

        try {
            await apolloClient.mutate({
                mutation: AddNewsletterSubscriptionDocument,
                variables: { email, region },
            });
            console.log("Newsletter-Abonnement erfolgreich hinzugefügt");
        } catch (e) {
            console.error(`Newsletter-Abonnement-Fehler: ${e}`);
        }
    }
);





function checkConditionalQuestions(state: QuestionnaireState) {
    // Handle conditional dependencies for all selected questionnaire types
    state.selectedQuestionnaireTypes.forEach(type => {
        const advisorConfig = AdvisorConfigs.find((c) => c.id === type);
        if (!advisorConfig?.conditionalDependencies) return;

        advisorConfig.conditionalDependencies.forEach(rule => {
            const shouldInclude = rule.requiredAnswers.every(condition => {
                const answer = state.answers[condition.question];
                return answer?.some(a => condition.values.includes(a));
            });

            const isIncluded = state.filteredQuestions.includes(rule.questionId);

            if (shouldInclude && !isIncluded) {
                const insertIndex = rule.insertAfter
                    ? state.filteredQuestions.indexOf(rule.insertAfter) + 1
                    : state.filteredQuestions.length;
                state.filteredQuestions.splice(insertIndex, 0, rule.questionId);
            } else if (!shouldInclude && isIncluded) {
                state.filteredQuestions = state.filteredQuestions.filter(
                    q => q !== rule.questionId
                );
            }
        });
    });
}



const initialState: QuestionnaireState = {
    questionnaireId: null,
    state: QuestionnaireStates.Uninitialized,
    currentQuestion: 0,
    currentAdvisorConfig: undefined, // Initialisieren mit null
    answers: {},
    filteredQuestions: [], //Object.keys(QuestionnaireConfig),
    selectedQuestionnaireTypes: [QuestionnaireType.whole],
    oneTimeToken: null,
};

function setNextQuestionDefaultAnswer(state: QuestionnaireState) {
    if (state.currentQuestion !== state.filteredQuestions.length - 1) {
        const nextQuestion = QuestionnaireConfig[state.filteredQuestions[state.currentQuestion]];
        if (state.answers[nextQuestion.questionId] === undefined) {
            // state.answers[nextQuestion.questionId] = nextQuestion.defaultValue(state.answers);
            // console.log("Default: SET: ", nextQuestion.questionId, " to: ", nextQuestion.defaultValue(state.answers))
        }
    }
}

export const questionnaireSlice = createSlice({
    name: "questionnaire",
    initialState,
    reducers: {
        setOneTimeToken: (state, action: PayloadAction<string | null>) => {
            state.oneTimeToken = action.payload;
        },
        start: (
            state,
            action: PayloadAction<{ questionnaireTypes?: QuestionnaireType[]; bicycleType?: QuestionnaireBicycleType }>
        ) => {
            console.log("Entering start method");
            const { questionnaireTypes, bicycleType } = action.payload;

            state.selectedQuestionnaireTypes = questionnaireTypes || [QuestionnaireType.whole];

            // Test the question flow merging
            if (state.selectedQuestionnaireTypes.length > 1) {
                testQuestionFlowMerging(state.selectedQuestionnaireTypes);
            }

            state.currentQuestion = 0;
            state.answers = {};
            state.questionnaireId = null;

            // Handle multiple questionnaire types by merging their question flows
            if (state.selectedQuestionnaireTypes.length === 1) {
                // Single questionnaire type - use existing logic
                const advisorConfig = AdvisorConfigs.find((c) => c.id === state.selectedQuestionnaireTypes[0]);
                state.currentAdvisorConfig = advisorConfig;
                state.filteredQuestions = advisorConfig?.questionFlow ?? [];
            } else {
                // Multiple questionnaire types - merge question flows
                const allQuestionFlows: string[] = [];
                const seenQuestions = new Set<string>();
                
                console.log("Processing multiple questionnaire types:", state.selectedQuestionnaireTypes);
                
                // Collect all questions from all selected questionnaire types
                state.selectedQuestionnaireTypes.forEach(type => {
                    const advisorConfig = AdvisorConfigs.find((c) => c.id === type);
                    if (advisorConfig) {
                        advisorConfig.questionFlow.forEach(questionId => {
                            // Skip privacy_agreement during initial collection - we'll add it at the end
                            // This ensures privacy_agreement is always the last question, regardless of
                            // where it appears in individual questionnaire flows
                            if (questionId !== "privacy_agreement" && !seenQuestions.has(questionId)) {
                                seenQuestions.add(questionId);
                                allQuestionFlows.push(questionId);
                            }
                        });
                    } else {
                        console.warn(`No advisor config found for type: ${type}`);
                    }
                });
                
                // Always add privacy_agreement at the end if any questionnaire type includes it
                const hasPrivacyQuestion = state.selectedQuestionnaireTypes.some(type => {
                    const advisorConfig = AdvisorConfigs.find((c) => c.id === type);
                    return advisorConfig?.questionFlow.includes("privacy_agreement");
                });
                
                if (hasPrivacyQuestion) {
                    allQuestionFlows.push("privacy_agreement");
                }
                
                // Use the first advisor config as the primary one for conditional dependencies
                const primaryAdvisorConfig = AdvisorConfigs.find((c) => c.id === state.selectedQuestionnaireTypes[0]);
                state.currentAdvisorConfig = primaryAdvisorConfig;
                state.filteredQuestions = allQuestionFlows;
                console.log("Final merged question flow length:", state.filteredQuestions.length, "Questions:", state.filteredQuestions);
            }

            // Handle bicycleType
            if (bicycleType) {
                const bicycleTypeValue = QuestionnaireBicycleType[bicycleType as keyof typeof QuestionnaireBicycleType];

                // Pre-fill the bicycle_type answer
                state.answers["bicycle_type"] = [bicycleTypeValue];

                // Remove the bicycle_type question from filteredQuestions
                state.filteredQuestions = state.filteredQuestions.filter(
                    (qId) => qId !== "bicycle_type"
                );
                // Wichtig: Wenn die erste Frage entfernt wurde, stellen wir sicher,
                // dass wir tatsächlich bei der neuen ersten Frage starten
                state.currentQuestion = 0;
            }

            // Set default answer for the next question
            setNextQuestionDefaultAnswer(state);
            console.log("After Start method");
        },
        reset: (state) => {
            console.log("Entering reset method");

            state.currentQuestion = 0;
            state.answers = {};
            state.questionnaireId = null;
            state.filteredQuestions = Object.keys(QuestionnaireConfig);
            state.selectedQuestionnaireTypes = [QuestionnaireType.whole];

            setNextQuestionDefaultAnswer(state);
        },
        filterRelevantQuestions: (state) => {
            if (state.selectedQuestionnaireTypes.length === 1) {
                // Single questionnaire type - use existing logic
                if (state.currentAdvisorConfig) {
                    state.filteredQuestions = state.currentAdvisorConfig.questionFlow;
                } else {
                    console.error("Keine Advisor-Konfiguration gefunden.");
                }
            } else {
                // Multiple questionnaire types - merge question flows
                const allQuestionFlows: string[] = [];
                const seenQuestions = new Set<string>();
                
                console.log("filterRelevantQuestions: Re-merging for types:", state.selectedQuestionnaireTypes);
                
                // Collect all questions from all selected questionnaire types
                state.selectedQuestionnaireTypes.forEach(type => {
                    const advisorConfig = AdvisorConfigs.find((c) => c.id === type);
                    if (advisorConfig) {
                        advisorConfig.questionFlow.forEach(questionId => {
                            // Skip privacy_agreement during initial collection - we'll add it at the end
                            if (questionId !== "privacy_agreement" && !seenQuestions.has(questionId)) {
                                seenQuestions.add(questionId);
                                allQuestionFlows.push(questionId);
                            }
                        });
                    } else {
                        console.warn(`filterRelevantQuestions: No advisor config found for type: ${type}`);
                    }
                });
                
                // Always add privacy_agreement at the end if any questionnaire type includes it
                const hasPrivacyQuestion = state.selectedQuestionnaireTypes.some(type => {
                    const advisorConfig = AdvisorConfigs.find((c) => c.id === type);
                    return advisorConfig?.questionFlow.includes("privacy_agreement");
                });
                
                if (hasPrivacyQuestion) {
                    allQuestionFlows.push("privacy_agreement");
                }
                
                state.filteredQuestions = allQuestionFlows;
                console.log("filterRelevantQuestions: Re-merged question flow length:", state.filteredQuestions.length);
            }
        },
        // Im questionnaireSlice.ts bei der nextQuestion-Funktion
        nextQuestion: (state) => {

            if (state.currentQuestion < state.filteredQuestions.length - 1) {
                state.currentQuestion++;
            }

            setNextQuestionDefaultAnswer(state);
            checkConditionalQuestions(state);
        },
        previousQuestion: (state) => {
            if (state.currentQuestion > 0) {
                state.currentQuestion--;
            }
        },
        updateQuestionAnswer: (state, action: PayloadAction<(string | number)[]>) => {
            const currentQuestionConfig = QuestionnaireConfig[state.filteredQuestions[state.currentQuestion]]
            const questionId = currentQuestionConfig.questionId;
            state.answers[questionId] = action.payload;
        },
        updateUserData: (state, action: PayloadAction<{field: string, value: string}>) => {
            const { field, value } = action.payload;
            state.answers[field] = [value];
        },
        toggleQuestionAnswer: (state, action: PayloadAction<string | number>) => {
            //FIXME find a more elegant solution for mapping question ids and answers
            const currentQuestionConfig = QuestionnaireConfig[state.filteredQuestions[state.currentQuestion]]
            const questionId = currentQuestionConfig.questionId;
            if (state.answers[questionId] === undefined) {
                state.answers[questionId] = [action.payload];
            } else {
                state.answers[questionId] = state.answers[questionId].includes(action.payload)
                    ? state.answers[questionId].filter((v) => v !== action.payload)
                    : [...state.answers[questionId], action.payload];
            }
        },
        enterNumericQuestionAnswer: (state, action: PayloadAction<KeyValuePair<string, number|undefined>>) => {
            const [inputId, value] = action.payload;

            //const currentQuestionConfig = QuestionnaireConfig[state.filteredQuestions[state.currentQuestion]]
            //let questionId = currentQuestionConfig.questionId;

            if(value === undefined) {
                state.answers[inputId] = []
            } else {
                state.answers[inputId] = [value]
            }

            //console.log("reading out the state answers: for that input config ", state.answers[inputId])
        },
        setQuestionnaireId: (state, action: PayloadAction<string | null>) => {
            state.questionnaireId = action.payload;
        },
    },

    extraReducers: (builder) => {
        builder
            .addCase(startQuestionnaire.pending, (state) => {
                state.state = QuestionnaireStates.Creating;
            })
            .addCase(startQuestionnaire.rejected, (state) => {
                state.state = QuestionnaireStates.CreateError;
            })
            .addCase(startQuestionnaire.fulfilled, (state, action: PayloadAction<string>) => {
                state.questionnaireId = action.payload;
                state.state = QuestionnaireStates.Created;
            })
            .addCase(startQuestionnaireFromUrl.pending, (state) => {
                state.state = QuestionnaireStates.Creating;
            })
            .addCase(startQuestionnaireFromUrl.rejected, (state) => {
                state.state = QuestionnaireStates.CreateError;
            })
            .addCase(startQuestionnaireFromUrl.fulfilled, (state, action: PayloadAction<string>) => {
                state.questionnaireId = action.payload;
                state.state = QuestionnaireStates.Created;
            })
            .addCase(submitAnswers.pending, (state) => {
                state.state = QuestionnaireStates.Submitting;
            })
            .addCase(submitAnswers.rejected, (state) => {
                state.state = QuestionnaireStates.SubmitError;
            })
            .addCase(submitAnswers.fulfilled, (state) => {
                state.state = QuestionnaireStates.Submitted;
            });
    },
});

export const {
    // startQuestionnaire,
    setOneTimeToken,
    reset,
    start,
    filterRelevantQuestions,
    nextQuestion,
    previousQuestion,
    updateQuestionAnswer,
    updateUserData,
    toggleQuestionAnswer,
    enterNumericQuestionAnswer,
    setQuestionnaireId
    // completeQuestionnaire
} =
    questionnaireSlice.actions;

export const selectCurrentQuestionIndex = (state: RootState) => state.questionnaire.currentQuestion;

export const selectCurrentQuestionId = (state: RootState) => {
    const questionKey = state.questionnaire.filteredQuestions[state.questionnaire.currentQuestion];
    // Use the questionKey to get the full config
    const questionConfig = QuestionnaireConfig[questionKey];
    // Now extract the actual questionId property
    const questionId = questionConfig.questionId;
    // console.log("selectCurrentQuestionId:", questionKey, "=>", questionId);
    return questionId;
};

export const selectCurrentChoiceQuestionAnswers = (state: RootState) => {
    //TODO maybe it's smarter to reference question by index?
    const currentQuestionId = QuestionnaireConfig[state.questionnaire.filteredQuestions[state.questionnaire.currentQuestion]].questionId

    //const questionType = QuestionnaireConfig[state.questionnaire.filteredQuestions[state.questionnaire.currentQuestion]].questionType
    // THIS doesnt matter if(questionType === QuestionType.numeric || questionType === QuestionType.multiNumeric) {

    return state.questionnaire.answers[currentQuestionId] ?? [];
};

export const selectCurrentNumericQuestionAnswers = (state: RootState) => {
    const currentQuestion = QuestionnaireConfig[state.questionnaire.filteredQuestions[state.questionnaire.currentQuestion]];

    // Get all input IDs for the current question
    const inputIds = currentQuestion.inputConfigs?.map(config => config.inputId) || [];

    // Create an object with all answers for the current input IDs
    const answers: Record<string, number> = {};
    inputIds.forEach(inputId => {
        const answer = state.questionnaire.answers[inputId];
        if (answer && answer.length > 0) {
            answers[inputId] = answer[0] as number;
        }
    });

    return answers;
};


export const selectAllAnswers = (state: RootState) => state.questionnaire.answers;

export const selectQuestionnaireState = (state: RootState) => state.questionnaire.state;

export const selectIsCreating = (state: RootState) => state.questionnaire.state === QuestionnaireStates.Creating;

export const selectedArticleGroups = (state: RootState) => state.questionnaire.answers.article_groups

export const filteredQuestions = (state: RootState) => state.questionnaire.filteredQuestions

export const startQuestionnaireFromUrl = createAsyncThunk<
    string,
    void
>(
    "questionnaire/startFromUrl",
    async (_, thunkAPI): Promise<string> => {
        const params = new URLSearchParams(window.location.search);
        const questionnaireTypeParam = params.get("questionnaireType");
        const questionnaireTypesParam = params.get("questionnaireTypes");
        const bicycleTypeParam = params.get("bicycleType") as QuestionnaireBicycleType | null;

        let questionnaireTypes: QuestionnaireType[] = [QuestionnaireType.whole];
        
        if (questionnaireTypesParam) {
            // Handle multiple questionnaire types
            questionnaireTypes = questionnaireTypesParam.split(',').map(type => type as QuestionnaireType);
        } else if (questionnaireTypeParam) {
            // Handle single questionnaire type
            questionnaireTypes = [questionnaireTypeParam as QuestionnaireType];
        }

        return thunkAPI.dispatch(startQuestionnaire({ questionnaireTypes, bicycleType: bicycleTypeParam || undefined })).unwrap();
    }
);

export default questionnaireSlice.reducer;

// Temporary test function to verify question flow merging
export function testQuestionFlowMerging(questionnaireTypes: QuestionnaireType[]): string[] {
    const allQuestionFlows: string[] = [];
    const seenQuestions = new Set<string>();
    
    console.log("Testing question flow merging for types:", questionnaireTypes);
    
    questionnaireTypes.forEach(type => {
        const advisorConfig = AdvisorConfigs.find((c) => c.id === type);
        if (advisorConfig) {
            console.log(`Questions from ${type}:`, advisorConfig.questionFlow);
            advisorConfig.questionFlow.forEach(questionId => {
                // Skip privacy_agreement during initial collection - we'll add it at the end
                // This ensures privacy_agreement is always the last question, regardless of
                // where it appears in individual questionnaire flows
                if (questionId !== "privacy_agreement" && !seenQuestions.has(questionId)) {
                    seenQuestions.add(questionId);
                    allQuestionFlows.push(questionId);
                }
            });
        }
    });
    
    // Always add privacy_agreement at the end if any questionnaire type includes it
    const hasPrivacyQuestion = questionnaireTypes.some(type => {
        const advisorConfig = AdvisorConfigs.find((c) => c.id === type);
        return advisorConfig?.questionFlow.includes("privacy_agreement");
    });
    
    if (hasPrivacyQuestion) {
        allQuestionFlows.push("privacy_agreement");
        console.log("Added privacy_agreement at the end");
    }
    
    console.log("Final merged result:", allQuestionFlows);
    console.log("Privacy question position:", allQuestionFlows.indexOf("privacy_agreement"));
    console.log("Total questions:", allQuestionFlows.length);
    
    return allQuestionFlows;
}
